import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {RESELECT_ITEM_EVENT_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {v4 as uuidv4} from 'uuid';
import {calculatePageDuration} from '@PosterWhiteboard/libraries/video-page';
import {PageItems} from '@PosterWhiteboard/page/page-items.class';
import {PageBackground} from '@PosterWhiteboard/page/page-background.class';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {CANVAS_BACKDROP_CLASS} from '@PosterWhiteboard/page/page.types';
import {initTransformDetail} from '@PosterWhiteboard/page/item-transform-details';
import {RenderBleed} from '@PosterWhiteboard/page/render-bleed.class';
import {RenderGrid} from '@PosterWhiteboard/page/render-grid.class';
import {RenderAlignmentGuidelines} from '@PosterWhiteboard/page/render-alignment-guidelines.class';
import {RenderFold} from '@PosterWhiteboard/page/render-fold.class';
import {PageSpellCheck} from '@PosterWhiteboard/page/page-spellcheck.class';
import {updatePopUpStateAndPosition} from '@Components/poster-editor/components/poster-popup-menu/poster-popup-menu-slice';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {SelectionManager} from '@PosterWhiteboard/page/active-selection.class';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHex} from '@Utils/color.util';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {PageClipboard} from '@PosterWhiteboard/page/page-clipboard';
import {PageSnapshot} from '@PosterWhiteboard/page/page-snapshot.class';
import {PageSVG} from '@PosterWhiteboard/page/page-svg.class';
import {PageWatermark} from '@PosterWhiteboard/page/page-watermark.class';
import {handleTextSelectionPopupMenu, updateTextSelection} from '@Libraries/text-selection-library';
import {isMobile} from 'react-device-detect';
import {updateActiveItemsOnPage, updateHoveredItem, updateSidebarState} from '@Components/poster-editor/poster-editor-reducer';
import {openPosterEditorMoreOptionsModal} from '@Modals/poster-editor-more-options-modal/poster-editor-more-options-modal';
import {CanvasPanZoom} from '@PosterWhiteboard/page/page-canvas-pan-zoom.class';
import {CanvasPanOnTextEdit} from '@PosterWhiteboard/page/page-canvas-pan-on-text-edit.class';
import {getFabricObjectUID, type ItemFabricObject} from '@PosterWhiteboard/items/item/item.class';
import {redirectUser} from '@Utils/browser.util';
import type {CanvasEvents, FabricImage, FabricObject, Group, TFiller} from '@postermywall/fabricjs-2';
import {ActiveSelection, Canvas, StaticCanvas, Textbox, util} from '@postermywall/fabricjs-2';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {moveItemToDifferentPage, isEditorMobileVariant, isFabricObjectOnPageCanvas, pasteStylesOnMouseDown} from '@Components/poster-editor/library/poster-editor-library';
import Emitter from '@/services/event-emitter';
import type {DeepPartial} from '@/global';
import type {CornerPoints} from '@/utils/math.util';
import 'hammerjs';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import debounce from 'lodash/debounce';
import {noop} from '@Utils/general.util';
import {PMWFabricLine} from '@PosterWhiteboard/items/line-item/pmw-fabric-line';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import {PageIntroAnimation} from '@PosterWhiteboard/page/animation/page-intro-animation.class';
import {PageOutroAnimation} from '@PosterWhiteboard/page/animation/page-outro-animation.class';
import tinycolor from 'tinycolor2';

const DEFAULT_PAGE_DURATION = 5;

export interface AddItemOpts {
  undoable?: boolean;
  updateRedux?: boolean;
  fireEvent?: boolean;
  zIndex?: number;
  checkForDurationUpdate?: boolean;
}

export class Page extends CommonMethods {
  public hashedID = '';
  public fabricCanvas!: Canvas | StaticCanvas;
  public fabricCanvasHammerManager?: HammerManager;
  public hammerEventFunctions;
  public background!: PageBackground;
  public items!: PageItems;
  public introAnimation!: PageIntroAnimation;
  public outroAnimation!: PageOutroAnimation;
  public poster: Poster;
  public duration = 0;
  public renderAlignmentGuidelines: RenderAlignmentGuidelines;
  public renderBleed: RenderBleed;
  public renderGrid: RenderGrid;
  public renderFold: RenderFold;
  public pageWatermark: PageWatermark;
  public pageSnapshot: PageSnapshot;
  public pageSVG: PageSVG;
  public spellCheck: PageSpellCheck;
  public clipboard: PageClipboard;
  public activeSelection: SelectionManager;
  public canvasPanZoom: CanvasPanZoom;
  public canvasPanOnTextEdit: CanvasPanOnTextEdit;
  public longPressInitiated: boolean;
  private mouseOverObject: FabricObject | null = null;
  private readonly delayedCanvasRefresh;
  private canvasBackdrop!: HTMLDivElement | null;
  private canvasContainer!: HTMLDivElement;

  public constructor(poster: Poster, hashedID?: string) {
    super();
    this.poster = poster;
    this.hashedID = hashedID ?? uuidv4();
    this.background = new PageBackground(this);
    this.introAnimation = new PageIntroAnimation(this);
    this.outroAnimation = new PageOutroAnimation(this);
    this.items = new PageItems(this);
    this.activeSelection = new SelectionManager(this);
    this.canvasPanZoom = new CanvasPanZoom(this);
    this.canvasPanOnTextEdit = new CanvasPanOnTextEdit(this);
    this.longPressInitiated = false;
    this.duration = DEFAULT_PAGE_DURATION;
    this.hammerEventFunctions = {
      longpress: this.openTouchContextualMenu.bind(this),
      singletap: this.onSingleTapOnCanvas.bind(this),
      doubletap: this.onCanvasDoubleTap.bind(this),
      singleFingerPan: this.canvasPanZoom.onCanvasSingleFingerPan.bind(this.canvasPanZoom),
      twoFingerPan: this.canvasPanZoom.onCanvasTouchGesture.bind(this.canvasPanZoom),
      pinch: this.canvasPanZoom.onCanvasTouchGesture.bind(this.canvasPanZoom),
    };
    this.delayedCanvasRefresh = debounce(
      () => {
        this.refreshVisibleCanvas();
      },
      100,
      {
        leading: false,
        maxWait: 120,
      }
    );

    this.addCanvasToPoster();
    this.renderAlignmentGuidelines = new RenderAlignmentGuidelines(this);
    this.renderBleed = new RenderBleed(this);
    this.renderGrid = new RenderGrid(this);
    this.renderFold = new RenderFold(this);
    this.pageWatermark = new PageWatermark(this);
    this.pageSnapshot = new PageSnapshot(this);
    this.pageSVG = new PageSVG(this);
    if (this.fabricCanvas instanceof Canvas) {
      initTransformDetail(this.fabricCanvas);
    }
    this.spellCheck = new PageSpellCheck(this);
    this.clipboard = new PageClipboard(this);
    Emitter.on('user:loggedIn', this.onUserLoggedIn.bind(this));
  }

  public applyScale(): void {
    if (this.poster.hasFakeScrollbars()) {
      const whiteboardContainer = window.document.getElementById('poster-whiteboard-container');
      if (!whiteboardContainer) {
        return;
      }

      const newCanvasTotalWdith = this.poster.width * this.poster.scaling.scale;
      const newCanvasTotalHeight = this.poster.height * this.poster.scaling.scale;

      const newCanvasVisibleWidth = newCanvasTotalWdith < whiteboardContainer.clientWidth ? newCanvasTotalWdith : whiteboardContainer.clientWidth;
      const newCanvasVisibleHeigth = newCanvasTotalHeight < whiteboardContainer.clientHeight ? newCanvasTotalHeight : whiteboardContainer.clientHeight;

      this.fabricCanvas.setDimensions({
        width: newCanvasVisibleWidth,
        height: newCanvasVisibleHeigth,
      });
      this.fabricCanvas.setZoom(this.poster.scaling.scale);
    } else {
      if (this.poster.mode.isGeneration()) {
        this.fabricCanvas.setDimensions({
          width: this.poster.width * this.poster.scaling.scale,
          height: this.poster.height * this.poster.scaling.scale,
        });
        this.fabricCanvas.setZoom(this.poster.scaling.scale);
      } else {
        this.fabricCanvas.setZoom(this.poster.scaling.scale);
        this.updateBackdropDimensions();
        this.fabricCanvas.setDimensions({
          width: this.poster.width * this.poster.scaling.scale,
          height: this.poster.height * this.poster.scaling.scale,
        });
        this.refreshVisibleCanvas();
        this.delayedCanvasRefresh();
      }
    }

    this.fabricCanvas.requestRenderAll();
  }

  public refreshVisibleCanvas(): void {
    const whiteboardContainer = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER]?.get(0);

    if (!whiteboardContainer || !this.canvasBackdrop || !this.canvasContainer) return;

    const whiteboardContainerRect = whiteboardContainer.getBoundingClientRect();
    const backdropRect = this.canvasBackdrop.getBoundingClientRect();

    this.canvasContainer.style.top = `${backdropRect.top <= whiteboardContainerRect.top ? -(backdropRect.top - whiteboardContainerRect.top) : 0}px`;
    this.canvasContainer.style.left = `${backdropRect.left - whiteboardContainerRect.left < 0 ? -(backdropRect.left - whiteboardContainerRect.left) : 0}px`;

    const backdropVisibleTop = backdropRect.top <= whiteboardContainerRect.top ? whiteboardContainerRect.top : backdropRect.top;
    const backdropVisibleBottom = backdropRect.bottom > whiteboardContainerRect.bottom ? whiteboardContainerRect.bottom : backdropRect.bottom;
    const backdropVisibleHeight = backdropVisibleBottom - backdropVisibleTop;
    const backdropVisibleY = backdropRect.top < whiteboardContainerRect.top ? -(backdropRect.top - whiteboardContainerRect.top) : 0;

    const backdropVisibleLeft = backdropRect.left < whiteboardContainerRect.left ? whiteboardContainerRect.left : backdropRect.left;
    const backdropVisibleRight = backdropRect.right > whiteboardContainerRect.right ? whiteboardContainerRect.right : backdropRect.right;
    const backdropVisibleWidth = backdropVisibleRight - backdropVisibleLeft;
    const backdropVisibleX = backdropRect.left < whiteboardContainerRect.left ? backdropRect.left - whiteboardContainerRect.left : 0;

    this.fabricCanvas.viewportTransform[4] = backdropVisibleX;
    this.fabricCanvas.viewportTransform[5] = -backdropVisibleY;
    this.fabricCanvas.setViewportTransform(this.fabricCanvas.viewportTransform);
    this.fabricCanvas.setDimensions({
      width: backdropVisibleWidth,
      height: backdropVisibleHeight,
    });
  }

  private setDimensionsForCanvas(): void {
    const whiteboardContainer = window.document.getElementById('poster-whiteboard-container');
    if (!whiteboardContainer) {
      return;
    }

    this.fabricCanvas.setDimensions({
      width: Math.min(this.poster.width * this.poster.scaling.scale, whiteboardContainer.clientWidth),
      height: Math.min(this.poster.height * this.poster.scaling.scale, whiteboardContainer.clientHeight),
    });
  }

  public updateBackdropDimensions(): void {
    const whiteboardContainer = window.document.getElementById('poster-whiteboard-container');
    if (!whiteboardContainer) {
      return;
    }

    const targetDiv = $(whiteboardContainer).find(`div.pmw-page-container[data-id="${this.hashedID}"]`);
    const backdrop = targetDiv.find('div.pmw-canvas-backdrop').get(0);
    if (backdrop) {
      backdrop.style.width = `${this.poster.width * this.poster.scaling.scale}px`;
      backdrop.style.height = `${this.poster.height * this.poster.scaling.scale}px`;
    }
  }

  public async addItem(newItem: ItemType, {updateRedux = true, fireEvent = true, undoable = true, zIndex, checkForDurationUpdate = false}: AddItemOpts = {}): Promise<void> {
    newItem.page = this;
    this.items.addItem(newItem);
    if (zIndex !== undefined) {
      this.fabricCanvas.insertAt(zIndex, newItem.fabricObject);
    } else {
      this.fabricCanvas.add(newItem.fabricObject);
    }
    await newItem.onItemAddedToPage();
    if (!this.poster.mode.isGeneration()) {
      this.fabricCanvas.requestRenderAll();
    }
    if (checkForDurationUpdate) {
      this.checkForPageDurationUpdate(newItem);
    }

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
    if (fireEvent) {
      this.poster.fire('modified');
    }
  }

  public isMemoryIntensive(): boolean {
    const amountOfHeavyItems = this.items.getImageItems().length + this.items.getSlideshowItems().length + this.items.getVideoItems().length;
    if (this.isVideo()) {
      return amountOfHeavyItems > 5;
    }
    return amountOfHeavyItems > 30;
  }

  private onUserLoggedIn(): void {
    this.pageWatermark.refreshWatermark();
  }

  private checkForPageDurationUpdate(item: ItemType): void {
    if (item.isVideo() || item.isSticker() || item.isTranscript() || (item.isSlideshow() && item.isStreamingItem())) {
      this.calculateAndUpdatePageDuration();
    }
  }

  public getNonScaledCanvasScaleddWidth(): number {
    return this.fabricCanvas.width / this.fabricCanvas.getZoom();
  }

  public getNonScaledCanvasScaleddHeight(): number {
    return this.fabricCanvas.height / this.fabricCanvas.getZoom();
  }

  public calculateAndUpdatePageDuration({updateRedux = false, undoable = false, ...opts}: UpdateFromObjectOpts = {}): void {
    const updatedDuration = calculatePageDuration(this);
    if (updatedDuration < this.duration && this.poster.getCurrentTime() > updatedDuration) {
      void this.poster.stop();
    }
    this.items.updateEndTimesForUpdatedDuration(updatedDuration, this.getDuration());

    void this.updateFromObject(
      {
        duration: updatedDuration,
      },
      {updateRedux, undoable, ...opts}
    );
  }

  public isExpensiveVideo(): boolean {
    return this.items.hasVideoItem() || this.items.hasVideoContainingSlideshowItem();
  }

  public getDuration(): number {
    return this.duration;
  }

  private onMouseDown(e: CanvasEvents['mouse:down']): void {
    this.poster.audioClips.unselectAudioPlaylist();
    if (this.poster.mode.isWebpage()) {
      this.openClickableLinkOnItem(e);
    }
    if (this.poster.isPlaying()) {
      void this.poster.pause();
    }
  }

  private onMouseUp(): void {
    this.poster.itemsMultiSelect.onMouseUp();
    this.longPressInitiated = false;
    this.canvasPanZoom.onMouseUp();
  }

  private openClickableLinkOnItem(e: CanvasEvents['mouse:down']): void {
    if (e.target) {
      const item = this.items.getItemForFabricObject(e.target);
      if (item?.hasClickableLink()) {
        redirectUser(item.clickableLink, false, false);
      }
    }
  }

  public getItemLinksMap(): Record<string, string> {
    const uidLinkMap: Record<string, string> = {};

    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.hasClickableLink()) {
        uidLinkMap[item.uid] = item.clickableLink;
      }
    }
    return uidLinkMap;
  }

  public isTimeWithinPageStartAndEndTimes(time: number): boolean {
    const pageStartTime = this.poster.pages.getStartTimeForPage(this.hashedID) ?? 0;
    const pageEndTime = this.poster.pages.getEndTimeForPage(this.hashedID) ?? 0;

    return time >= pageStartTime && time <= pageEndTime;
  }

  public getStartTime(): number | undefined {
    return this.poster.pages.getStartTimeForPage(this.hashedID);
  }

  public getEndTime(): number | undefined {
    return this.poster.pages.getEndTimeForPage(this.hashedID);
  }

  private onBeforeSelectionCleared(fabricEvent: CanvasEvents['before:selection:cleared']): void {
    if (fabricEvent.e?.type === RESELECT_ITEM_EVENT_TYPE) {
      return;
    }
    for (const fabricObj of fabricEvent.deselected) {
      if (fabricObj instanceof ActiveSelection) {
        const activeObjects = fabricObj.getObjects();
        for (const activeObject of activeObjects) {
          this.items.getItem(activeObject.__PMWID)?.stopItemAnimation();
        }
      }
    }
  }

  private onSelectionCreated(fabricEvent: CanvasEvents['selection:updated']): void {
    if (fabricEvent.e?.type === RESELECT_ITEM_EVENT_TYPE) {
      return;
    }

    this.toggleActiveSelectionLockIfNeeded();
    this.updateSelectedPageIdForPoster(this.hashedID);
    this.activeSelection.onSelectionCreated();
    this.poster.audioClips.unselectAudioPlaylist();
    this.items.updateBoundItemsZIndices();
    void pasteStylesOnMouseDown();
    this.updateSelectedItemsInRedux();
  }

  private toggleActiveSelectionLockIfNeeded(): void {
    const activeSelection = this.activeSelection.getActiveObject();

    if (activeSelection instanceof ActiveSelection) {
      const activeObjects = activeSelection.getObjects();

      let lockAllObjects = false;

      for (const activeObject of activeObjects) {
        if (activeObject.isLocked()) {
          lockAllObjects = true;
          break;
        }
      }

      if (lockAllObjects) {
        activeSelection.set({lockMovementX: true, lockMovementY: true, lockScalingX: true, lockScalingY: true, lockRotation: true});
      } else {
        activeSelection.set({lockMovementX: false, lockMovementY: false, lockScalingX: false, lockScalingY: false, lockRotation: false});
      }
    }
  }

  private updateSelectedItemsInRedux(): void {
    window.PMW.redux.store.dispatch(
      updateActiveItemsOnPage({
        pageHashedId: this.hashedID,
        activeItemIds: this.getSelectedItemIds(),
      })
    );
  }

  private onSelectionCleared(fabricEvent: CanvasEvents['selection:cleared']): void {
    if (fabricEvent.e?.type === RESELECT_ITEM_EVENT_TYPE) {
      return;
    }

    this.updateSelectedPageIdForPoster(undefined);

    this.items.updateBoundItemsZIndices();
    this.poster.itemsMultiSelect.onSelectionCleared();
    this.updateSelectedItemsInRedux();
    this.fabricCanvas.requestRenderAll();
  }

  private updateSelectedPageIdForPoster(hashedId: string | undefined): void {
    if (hashedId === undefined) {
      this.clearSelectedPageIdForPoster();
    } else {
      this.poster.updateSelectedPageId(this.hashedID);
    }
  }

  private clearSelectedPageIdForPoster(): void {
    if (this.isSelected()) {
      this.poster.clearSelectedPageId();
    }
  }

  private isSelected(): boolean {
    return this.poster.selectedPageId === this.hashedID;
  }

  private onContextMenuStopped(data: CanvasEvents['contextmenu:before']): void {
    if (this.poster.mode.isWebpage()) {
      return;
    }
    this.selectTargetItem(data.target);
    this.openContextualMenu(data);
  }

  private openContextualMenu(data: CanvasEvents['contextmenu:before']): void {
    const {scale} = this.poster.scaling;
    const mouseEvent = data.e as MouseEvent;

    window.PMW.redux.store.dispatch(
      updatePopUpStateAndPosition({
        openPosterPopUpMenu: true,
        popUpPosition: {top: mouseEvent.pageY, left: mouseEvent.pageX},
        target: data.target,
        eventCanvasOffset: {top: mouseEvent.offsetY / scale, left: mouseEvent.offsetX / scale},
      })
    );
  }

  private openTouchContextualMenu(data: HammerInput): void {
    if (data.pointerType === 'mouse' || this.poster.mode.isWebpage()) {
      return;
    }
    this.longPressInitiated = true;

    const activeObject = this.activeSelection.getActiveObject();
    if (isMobile && activeObject instanceof Textbox && activeObject.isEditing) {
      updateTextSelection(activeObject);
      handleTextSelectionPopupMenu(activeObject);
      this.fabricCanvas.requestRenderAll();
    } else if (window.PMW.redux.store.getState().posterEditor.isMobileVariant) {
      if (!activeObject?.__corner) {
        openPosterEditorMoreOptionsModal();
      }
    } else {
      const {scale} = this.poster.scaling;
      window.PMW.redux.store.dispatch(
        updatePopUpStateAndPosition({
          openPosterPopUpMenu: true,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore. data.srcEvent is the source event and HammerInput assumes it can only be TouchEvent | MouseEvent | PointerEvent, while in this case it's a fabric event which is why we aren't getting types.
          popUpPosition: {top: data.srcEvent.pageY as number, left: data.srcEvent.pageX as number},
          target: this.activeSelection.getActiveObject(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          eventCanvasOffset: {top: data.srcEvent.offsetY / scale, left: data.srcEvent.offsetX / scale},
        })
      );
    }
  }

  private selectTargetItem(targetObject: FabricObject | undefined): void {
    if (targetObject && this.activeSelection.getActiveObjects().length < 2) {
      this.activeSelection.setActiveObject(targetObject);
    }
  }

  private addCanvasToPoster(): void {
    let html = ``;
    if (isEditorMobileVariant() || this.poster.mode.isGeneration()) {
      html = `<div class='pmw-page-container js-canvas' data-id='${this.hashedID}'>
                    <div class='pmw-pre-page-container'></div>
                    <canvas width='${this.poster.width}' height='${this.poster.height}'/>
                    <div class='pmw-post-page-container'></div>
                  </div>`;
    } else {
      html = `<div class='pmw-page-container js-canvas' data-id='${this.hashedID}'>
                    <div class='pmw-pre-page-container'></div>
                    <div class="flexbox flex-row inner-page-container">
                      <div class='${CANVAS_BACKDROP_CLASS} _position-relative' id='${this.hashedID}'>
                          <canvas width='${this.poster.width}' height='${this.poster.height}'/>
                      </div>
                      <div>
                        <div class="page-right-side-options"></div>
                      </div>
                    </div>
                    <div class='pmw-post-page-container'></div>
                  </div>`;
    }

    this.poster.HTMLElement.insertAdjacentHTML('beforeend', html);

    this.fabricCanvas = this.getFabricCanvas();

    this.fabricCanvas.on('mouse:down:before', this.poster.itemsMultiSelect.onMouseDownBefore.bind(this.poster.itemsMultiSelect));
    this.fabricCanvas.on('mouse:down', this.onMouseDown.bind(this));
    this.fabricCanvas.on('mouse:up', this.onMouseUp.bind(this));
    this.fabricCanvas.on('contextmenu:before', this.onContextMenuStopped.bind(this));
    this.fabricCanvas.on('path:created', (e) => {
      this.poster.drawing.onPathDrawn(e, this.hashedID);
    });
    this.fabricCanvas.on('after:touchstart', this.onAfterTouchStart.bind(this));
    this.fabricCanvas.on('before:render', this.clearContext.bind(this));

    const canvasContainer = this.getCanvasContainer() as HTMLDivElement;
    const backDrop = document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${this.hashedID}"] .pmw-canvas-backdrop`);
    if (!canvasContainer && !this.poster.mode.isGeneration()) {
      throw new Error('canvas container not found');
    }
    this.canvasBackdrop = backDrop;
    this.canvasContainer = canvasContainer;

    if (isEditorMobileVariant()) {
      canvasContainer.classList.add('mobile-canvas-container');
    }

    this.initSelectionEvents();
    this.initObjectModificationEvents();
    this.initHoverEvents();
    this.initUpperCanvasElEvents();

    this.initRenderOnEachFrame();
    this.initRenderOnWindowFocus();
    this.applyScale();
  }

  public removeHtml(): void {
    Page.getHTMLContainerForPageId(this.hashedID)?.remove();
  }

  public setPosterItemsModificationState = (modificationType: 'scaling' | 'moving' | 'rotating' | false): void => {
    this.poster.redux.setPosterItemsModificationState(modificationType, this.hashedID);
  };

  private initObjectModificationEvents(): void {
    this.fabricCanvas.on('object:moving', this.onObjectsModifying.bind(this, 'moving'));
    this.fabricCanvas.on('object:rotating', this.onObjectsModifying.bind(this, 'rotating'));
    this.fabricCanvas.on('object:scaling', this.onObjectsModifying.bind(this, 'scaling'));
    this.fabricCanvas.on('object:modified', this.onObjectsModified.bind(this));
  }

  private initSelectionEvents(): void {
    this.fabricCanvas.on('before:selection:cleared', this.onBeforeSelectionCleared.bind(this));
    this.fabricCanvas.on('selection:updated', this.onSelectionCreated.bind(this));
    this.fabricCanvas.on('selection:created', this.onSelectionCreated.bind(this));
    this.fabricCanvas.on('selection:cleared', this.onSelectionCleared.bind(this));
  }

  private initHoverEvents(): void {
    this.fabricCanvas.on('object:removed', this.onObjectRemoved.bind(this));
    this.fabricCanvas.on('mouse:over', this.onObjectMouseOver.bind(this));
    this.fabricCanvas.on('mouse:out', this.onObjectMouseOut.bind(this));
  }

  /**
   * Marks the object mouse is over and renders canvas to draw the borders around it
   * @param {object} e
   * @private
   */
  private onObjectMouseOver(e: CanvasEvents['mouse:over']): void {
    if (e.target && e.target.type !== 'activeSelection' && e.target.selectable && !(e.target instanceof PMWFabricLine)) {
      this.mouseOverObject = e.target;

      if (this.isFabricObjectCurrentlySelected(e.target) || e.target instanceof ActiveSelection || this.poster.redux.areAnyPosterItemsModifying()) {
        return;
      }

      const item = this.getItemForFabricObject(e.target);
      if (!item) {
        return;
      }

      window.PMW.redux.store.dispatch(
        updateHoveredItem({
          pageHashedId: this.hashedID,
          hoveredItemId: item.uid,
        })
      );

      this.fabricCanvas.renderAll();
    }
  }

  /**
   * Clears the mouseOverObject if it was the object removed
   * @private
   */
  private onObjectRemoved(e: CanvasEvents['object:removed']): void {
    if (e.target && e.target === this.mouseOverObject) {
      this.mouseOverObject = null;

      window.PMW.redux.store.dispatch(
        updateHoveredItem({
          pageHashedId: this.hashedID,
          hoveredItemId: undefined,
        })
      );
    }
  }

  /**
   * Marks the object mouse is over as null and renders canvas to clear any borders drawn
   * @private
   */
  private onObjectMouseOut(): void {
    this.mouseOverObject = null;
    this.fabricCanvas.renderAll();

    window.PMW.redux.store.dispatch(
      updateHoveredItem({
        pageHashedId: this.hashedID,
        hoveredItemId: undefined,
      })
    );
  }

  private isFabricObjectCurrentlySelected(fabricObject: FabricObject): boolean {
    const selectedFabricObjects: FabricObject[] = this.getSelectedItems().map((selectedItem) => {
      return selectedItem.fabricObject;
    });

    for (const fabricObj of selectedFabricObjects) {
      if (fabricObj === fabricObject) {
        return true;
      }
    }

    return false;
  }

  private clearContext(): void {
    if (this.fabricCanvas instanceof Canvas && this.fabricCanvas.contextTop) {
      this.fabricCanvas.contextTopDirty = true;
    }
  }

  private async onObjectsModified(e: CanvasEvents['object:modified']): Promise<void> {
    await this.handlePageChangeOnObjectsModified(e);

    this.setPosterItemsModificationState(false);
    this.poster.getCurrentPage().items.updateItemsAnimation();
    this.poster.history.addPosterHistory();
    this.poster.redux.updateReduxData();
    this.poster.fire('modified');
  }

  private async handlePageChangeOnObjectsModified(e: CanvasEvents['object:modified']): Promise<void> {
    if (!e.target) {
      return;
    }

    if (e.target instanceof ActiveSelection) {
      await this.handleActiveSelectionItemsPageChangeIfNeeded(e.target);
    } else {
      await this.handleItemPageChangedIfNeeded(e.target);
    }
  }

  private async handleItemPageChangedIfNeeded(selectedItemFabricObject: FabricObject): Promise<void> {
    const item = this.getItemForFabricObject(selectedItemFabricObject);
    if (!item) {
      return;
    }

    if (!isFabricObjectOnPageCanvas(selectedItemFabricObject, this.hashedID)) {
      const newPage = this.poster.pages.findPageContainingItem(selectedItemFabricObject);
      if (!newPage) {
        return;
      }

      const movedItemId = await moveItemToDifferentPage(this, newPage, item);
      if (movedItemId) {
        const newItem = newPage.items.getItem(movedItemId);
        if (newItem) {
          newPage.activeSelection.selectFabricObjects([newItem.fabricObject]);
        }
      }
    }
  }

  private async handleActiveSelectionItemsPageChangeIfNeeded(activeSelection: ActiveSelection): Promise<void> {
    const activeObjects = activeSelection.getObjects();
    const moveItemPromises: Promise<string | undefined>[] = [];

    for (const obj of activeObjects) {
      const item = this.getItemForFabricObject(obj);
      if (!item) {
        continue;
      }

      if (!isFabricObjectOnPageCanvas(obj, this.hashedID)) {
        const newPage = this.poster.pages.findPageContainingItem(obj);
        if (newPage) {
          moveItemPromises.push(moveItemToDifferentPage(this, newPage, item));
        }
      }
    }

    const movedUIDs = (await Promise.all(moveItemPromises)).filter((uid): uid is string => typeof uid === 'string');
    this.poster.selectMovedItems(movedUIDs);
  }

  private onObjectsModifying(modificationType: 'scaling' | 'moving' | 'rotating', e: CanvasEvents['object:moving' | 'object:rotating' | 'object:scaling']): void {
    if (e.target) {
      this.setPosterItemsModificationState(modificationType);
      if (e.target instanceof ActiveSelection) {
        const group = e.target;
        const activeObjects = group.getObjects();
        for (const activeObject of activeObjects) {
          const itemId = getFabricObjectUID(activeObject);
          const item = window.posterEditor?.whiteboard?.getItemForId(itemId);
          if (item) {
            this.onItemModifying(item, modificationType);
          }
        }
      } else {
        const itemId = getFabricObjectUID(e.target);
        const item = window.posterEditor?.whiteboard?.getItemForId(itemId);
        if (item) {
          this.onItemModifying(item, modificationType);
        }
      }
    }
  }

  private onItemModifying(item: ItemType, modificationType: 'scaling' | 'moving' | 'rotating'): void {
    switch (modificationType) {
      case 'moving':
        item.onMoving();
        return;
      case 'scaling':
        item.onScaling();
        return;
      case 'rotating':
        item.onRotating();
        return;
      default:
        throw new Error('Unhandled modificationType');
    }
  }

  private initRenderOnEachFrame(): void {
    if (!this.poster.mode.isGeneration()) {
      const render = (): void => {
        if (this.isVideo() && this.poster.isPlaying()) {
          this.fabricCanvas.renderAll();
          util.requestAnimFrame(render);
        } else {
          setTimeout(() => {
            util.requestAnimFrame(render);
          }, 100);
        }
      };
      util.requestAnimFrame(render);
    }
  }

  private initRenderOnWindowFocus(): void {
    window.addEventListener('focus', () => {
      this.fabricCanvas.requestRenderAll();
    });
  }

  private getFabricCanvas(): Canvas | StaticCanvas {
    if (this.poster.mode.isGeneration()) {
      return new StaticCanvas(this.getHTMLCanvas(), {
        renderOnAddRemove: false,
        skipControlsDrawing: true,
      });
    }
    return new Canvas(this.getHTMLCanvas(), {
      hoverCursor: 'pointer',
      selection: true,
      uniScaleKey: 'shiftKey',
      controlsAboveOverlay: true,
      preserveObjectStacking: true,
      allowTouchScrolling: true,
      stopContextMenu: true,
      targetFindTolerance: isMobile ? 50 : 10,
    });
  }

  private onCanvasDoubleTap(): void {
    const activeObject = this.activeSelection.getActiveObject();
    if (activeObject instanceof Textbox && activeObject.isEditing) {
      updateTextSelection(activeObject);
      handleTextSelectionPopupMenu(activeObject);
    } else {
      window.PMW.redux.store.dispatch(updateSidebarState(true));
    }
  }

  public async dispose(): Promise<void> {
    await this.fabricCanvas.dispose();
    for (const [eventName, eventFunction] of Object.entries(this.hammerEventFunctions)) {
      this.fabricCanvasHammerManager?.off(eventName, eventFunction as HammerListener);
    }
    this.fabricCanvasHammerManager?.destroy();
  }

  private onAfterTouchStart(e: CanvasEvents['after:touchstart']): void {
    this.poster.fire('fabricCanvas:touch:start');
    this.canvasPanZoom.onCanvasAfterTouchStart(e);
  }

  private initUpperCanvasElEvents(): void {
    if (this.fabricCanvas instanceof Canvas) {
      this.fabricCanvasHammerManager = new Hammer.Manager(this.fabricCanvas.upperCanvasEl);
      const longPress = new Hammer.Press({
        event: 'longpress',
        time: 900,
      });
      const singletap = new Hammer.Tap({
        event: 'singletap',
      });

      this.fabricCanvasHammerManager.add([longPress, singletap]);
      this.fabricCanvasHammerManager.on('longpress', this.hammerEventFunctions.longpress);
      this.fabricCanvasHammerManager.on('singletap', this.hammerEventFunctions.singletap);

      if (isMobile) {
        const doubleTap = new Hammer.Tap({
          event: 'doubletap',
          taps: 2,
        });
        this.fabricCanvasHammerManager.add([doubleTap]);
        this.fabricCanvasHammerManager.on('doubletap', this.hammerEventFunctions.doubletap);
      }

      if (isEditorMobileVariant()) {
        const singleFingerPan = new Hammer.Pan({
          event: 'singleFingerPan',
          direction: Hammer.DIRECTION_ALL,
          pointers: 1,
        });
        const twoFingerPan = new Hammer.Pan({
          event: 'twoFingerPan',
          direction: Hammer.DIRECTION_ALL,
          pointers: 2,
        });
        const pinch = new Hammer.Pinch({
          event: 'pinch',
        });

        this.fabricCanvasHammerManager.add([pinch, singleFingerPan, twoFingerPan]);
        this.fabricCanvasHammerManager.on('singleFingerPan', this.hammerEventFunctions.singleFingerPan);
        this.fabricCanvasHammerManager.on('twoFingerPan', this.hammerEventFunctions.twoFingerPan);
        this.fabricCanvasHammerManager.on('pinch', this.hammerEventFunctions.pinch);
      }
    }
  }

  private onSingleTapOnCanvas(): void {
    const canvas = this.fabricCanvas as Canvas;
    const activeObject = canvas.getActiveObject();
    const activeItem = this.getSelectedItems()[0];
    if (activeObject && activeObject instanceof Textbox && (activeItem.isText() || activeItem.isSlideshow()) && activeItem.clone && activeItem.clone.hiddenTextarea) {
      activeItem.clone.hiddenTextarea.focus();
    }
  }

  public static getHTMLContainerForPageId(pageId: string): HTMLDivElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"]`) ?? undefined;
  }

  public static getFirstCanvasContainerHTML(): HTMLElement | undefined {
    const elements = document.querySelectorAll<HTMLDivElement>('.pmw-page-container .canvas-container');
    return elements.length > 0 ? elements[0] : undefined;
  }

  public static getCanvasContainerHTMLForPageId(pageId: string): HTMLElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"] .canvas-container`) ?? undefined;
  }

  public static getPrePageHTMLContainerForPageId(pageId: string): HTMLElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"] .pmw-pre-page-container`) ?? undefined;
  }

  public static getPageRightSideHtmlContainerForPageId(pageId: string): HTMLElement | undefined {
    return document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${pageId}"] .page-right-side-options`) ?? undefined;
  }

  public getHTMLCanvas(): HTMLCanvasElement {
    const htmlCanvas = document.querySelector<HTMLCanvasElement>(`.pmw-page-container[data-id="${this.hashedID}"] canvas`);
    if (!htmlCanvas) {
      throw new Error('Canvas not found');
    }
    return htmlCanvas;
  }

  public getCanvasContainer(): HTMLElement | undefined {
    return Page.getCanvasContainerHTMLForPageId(this.hashedID);
  }

  public getDuplicatedPageObject(): PageObject {
    return this.toObjectWithNewUids();
  }

  public toObject(): PageObject {
    return {
      hashedID: this.hashedID,
      items: this.items.toObject(),
      background: this.background.toObject(),
      introAnimation: this.introAnimation.toObject(),
      outroAnimation: this.outroAnimation.toObject(),
      duration: this.duration,
    };
  }

  public toObjectWithNewUids(): PageObject {
    return {...this.toObject(), hashedID: uuidv4(), background: this.background.toObjectWithNewUids(), items: this.items.toObjectWithNewUids()};
  }

  public async updateFromObject(
    pageObject: DeepPartial<PageObject> = {},
    {updateRedux = true, undoable = true, fireEvent = true, refreshActiveSelection = false, checkForDurationUpdate = false, onItemAddFail = noop}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    const {items, background, introAnimation, outroAnimation, ...pageObjectWithoutItems} = pageObject;
    const oldDuration = this.duration;
    this.copyVals({
      ...pageObjectWithoutItems,
    });
    if (introAnimation) {
      this.introAnimation.updateFromObject({...introAnimation}, {undoable: false, checkForDurationUpdate});
    }
    if (outroAnimation) {
      this.outroAnimation.updateFromObject({...outroAnimation}, {undoable: false, checkForDurationUpdate});
    }
    if (background) {
      await this.background.updateFromObject(background, {undoable: false, updateRedux: false});
    }
    if (items) {
      await this.items.updateFromObject(items, {
        undoable: false,
        updateRedux: false,
        fireEvent: false,
        refreshActiveSelection,
        checkForDurationUpdate,
        onItemAddFail,
      });
    }

    if (pageObject.duration) {
      await this.poster.audioClips.trimAudiosExceedingPageDuration();
      await this.items.trimAllTranscriptItemsExceedingPageDuration();
      this.items.updateEndTimesForUpdatedDuration(pageObject.duration, oldDuration);
      await this.poster.updateCurrentTimeIfNeeded();
    }

    this.pageWatermark.refreshWatermark();
    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
    if (fireEvent) {
      this.poster.fire('modified');
    }
  }

  public selectAllUnlockedItems = (): void => {
    const fabricObjects = this.fabricCanvas.getObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }
    this.activeSelection.clearSelection();
    const objects: ItemFabricObject[] = [];
    for (const fabricObj of fabricObjects) {
      const item = this.items.getItem(fabricObj.__PMWID);
      if (item && !item.fabricObject.lockMovementX) {
        objects.push(fabricObj);
      }
    }

    this.activeSelection.selectFabricObjects(objects);
  };

  public selectAllItems = (): void => {
    const fabricObjects = this.fabricCanvas.getObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }
    this.activeSelection.clearSelection();

    const objects: ItemFabricObject[] = [];
    for (const fabricObj of fabricObjects) {
      const item = this.items.getItem(fabricObj.__PMWID);
      if (item) {
        objects.push(fabricObj);
      }
    }

    this.activeSelection.selectFabricObjects(objects);
  };

  public getSelectedItems(): ItemType[] {
    const fabricObjects = this.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return [];
    }

    const selectedItems = [];
    for (const fabricObj of fabricObjects) {
      const item = this.getItemForFabricObject(fabricObj);
      if (item) {
        selectedItems.push(item);
      }
    }
    return selectedItems;
  }

  public getSelectedItemIds(): string[] {
    const fabricObjects = this.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return [];
    }

    const selectedItemIds = [];
    for (const fabricObj of fabricObjects) {
      const item = this.getItemForFabricObject(fabricObj);
      if (item) {
        selectedItemIds.push(item.uid);
      }
    }

    return selectedItemIds;
  }

  public getSelectedZIndexMovableItems(): ItemType[] {
    const fabricObjects = this.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return [];
    }

    const selectedItems = [];
    for (const fabricObj of fabricObjects) {
      const item = this.getItemForFabricObject(fabricObj);
      if (item?.canMoveInZIndex()) {
        selectedItems.push(item);
      }
    }
    return selectedItems;
  }

  public getFilteredExistingViews(views: (FabricObject | Group)[]): (FabricObject | Group)[] {
    const filteredViews = [];
    for (const view of views) {
      if (this.fabricCanvas.getObjects().includes(view)) {
        filteredViews.push(view);
      }
    }
    return filteredViews;
  }

  public getItemForFabricObject(fabricObj: FabricObject): ItemType | undefined {
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.doesfabricObjBelongtoItem(fabricObj)) {
        return item;
      }
    }
    return undefined;
  }

  public getStreamingItems(): ItemType[] {
    const streamingItems = [];
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.isStreamingMediaItem()) {
        streamingItems.push(item);
      }
    }
    return streamingItems;
  }

  public hasItem(uid: string): boolean {
    return this.items.itemsHashMap[uid] !== undefined;
  }

  public isVideo(): boolean {
    if ((this.hasAnimation() && this.items.hasItems()) || this.items.pageItemHasAnimation() || this.poster.audioClips.hasAudio()) {
      return true;
    }
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.isVideo() || item.isSticker() || item.isTranscript() || (item.isSlideshow() && item.isStreamingItem())) {
        return true;
      }
    }
    return false;
  }

  public isTransparent(): boolean {
    return this.background.isTransparent();
  }

  public isPremium(): boolean {
    if (this.background.isPremium()) {
      return true;
    }

    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if (item.isPremium()) {
        return true;
      }
    }
    return false;
  }

  public async stopPage(): Promise<void> {
    const promises = [];
    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if ('stop' in item) {
        promises.push(item.stop());
      }
    }
    await Promise.all(promises);
  }

  public async playPage(): Promise<void> {
    const promises = [];

    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if ('play' in item) {
        promises.push(item.play());
      }
    }

    await Promise.all(promises);
  }

  public onSeekPage(): void {}

  public async pausePage(): Promise<void> {
    const promises = [];

    for (const [, item] of Object.entries(this.items.itemsHashMap)) {
      if ('pause' in item) {
        promises.push(item.pause());
      }
    }

    await Promise.all(promises);
  }

  public getCanvasCornerPoints(): CornerPoints {
    const {scale} = this.poster.scaling;
    return {
      tl: {
        x: 0,
        y: 0,
      },
      tr: {
        x: this.fabricCanvas.width / scale,
        y: 0,
      },
      br: {
        x: this.fabricCanvas.width / scale,
        y: this.fabricCanvas.height / scale,
      },
      bl: {
        x: 0,
        y: this.fabricCanvas.height / scale,
      },
    };
  }

  public setBackgroundColor(color: TFiller | string): void {
    this.fabricCanvas.backgroundColor = color;
  }

  public setBackgroundImage(image: FabricImage | undefined): void {
    this.fabricCanvas.backgroundImage = image;
  }

  public clearSelection(): void {
    this.activeSelection.clearSelection();
  }

  public getColorsOnPage(): string[] {
    let colors: RGBA[] = [];
    if (this.background.details.type === BackgroundTypeName.COLOR) {
      colors = [...this.background.details.fill.fillColor, ...colors];
    }

    const itemsOnPage = this.items.getItems();
    for (let i = 0; i < itemsOnPage.length; i += 1) {
      colors = [...itemsOnPage[i].getColors(), ...colors] as RGBA[];
    }

    const colorsOnDesign: string[] = [];
    for (const eachColor of colors) {
      colorsOnDesign.push(rgbaToHex(eachColor, false));
    }

    const uniqueColorsOnDesign = colorsOnDesign.filter((color, index) => {
      return colorsOnDesign.indexOf(color) === index;
    });

    return uniqueColorsOnDesign.sort((a, b) => {
      return tinycolor(a).getLuminance() - tinycolor(b).getLuminance();
    });
  }

  public getFontsOnPage(withVariation = false): string[] {
    const items = Object.values(this.items.itemsHashMap);
    const fonts: string[] = [];

    for (const eachItem of items) {
      fonts.push(...eachItem.getFonts(withVariation));
    }

    return fonts.filter((font, index) => {
      return fonts.indexOf(font) === index;
    });
  }

  public isSingleItemSelected(): boolean {
    const selectedItems = this.getSelectedItems();
    if (!selectedItems) {
      return false;
    }

    return selectedItems.length === 1;
  }

  public validatePremiumUserAccess(): boolean {
    return this.isPremium() && window.PMW.getUserPremiumLevel() === USER_PREMIUM_LEVELS.PAYG;
  }

  public async replacePageWithObject(pageObjectToReplaceWith: PageObject, undoable = false): Promise<void> {
    await this.updateFromObject(
      {
        introAnimation: pageObjectToReplaceWith.introAnimation,
        outroAnimation: pageObjectToReplaceWith.outroAnimation,
        background: pageObjectToReplaceWith.background,
        items: pageObjectToReplaceWith.items,
        duration: pageObjectToReplaceWith.duration,
      },
      {
        undoable,
      }
    );
  }

  public hasAnimation(): boolean {
    return this.hasIntroAnimation() || this.hasOutroAnimation();
  }

  public hasIntroAnimation(): boolean {
    return this.introAnimation.hasAnimation();
  }

  public hasOutroAnimation(): boolean {
    return this.outroAnimation.hasAnimation();
  }
}
